/**
* @module EquipmentListManager
* @description Component for displaying and managing equipment lists in the Conference Room Designer.
*/
import styled from 'styled-components';
import { useSelector } from "react-redux";
import PlainText from 'components/atoms/PlainText';
import Table from "components/molecules/Table";
import { getErrorState } from "reduxModules/ducks/ui";
import { getEquipmentList } from 'reduxModules/ducks/container';
import { keys, isEmpty } from 'lodash';

/**
* @function EquipmentListManager
* @description Renders a table of equipment with title and optional notes.
* Shows error message if design has validation errors.
* 
* @component
* @returns {React.Element} Equipment list display component
* 
* @description
* This component:
* 1. Displays equipment list data in a tabular format
* 2. Shows error message if design validation errors exist
* 3. Renders optional notes below the table
* 4. Handles empty data states
* 
* States handled:
* - Error state: Shows error message instead of table
* - Empty data: Renders nothing except title
* - Normal state: Renders title, table, and optional notes
* 
* @example
* // Example equipment list structure
* {
*   title: "Room Equipment List",
*   tableData: [
*     { item: "Speaker", quantity: 2, model: "SP100" },
*     { item: "Microphone", quantity: 1, model: "MC200" }
*   ],
*   widths: [40, 20, 40],
*   notes: ["Note 1", "Note 2"]
* }
*/
const EquipmentListManager = () => {
  const errorTitle = "can't display equipment list until design errors are cleared";
  const error = useSelector(getErrorState);
  const { title, tableData, widths, notes = [] } = useSelector(getEquipmentList);

  const tableTitle = error ? errorTitle : title;
  const emptyData = isEmpty(tableData);
  const headers = emptyData ? [] : keys(tableData[0]);

  return (
    <EquipmentListManager.Container>
      <PlainText className={`text-uppercase fw-bold mb-2 ${error ? 'text-center' : ''}`} size={22} color={'var(--app-red)'} content={tableTitle} />
      {!error && !emptyData && <Table widths={widths} headers={headers} content={tableData} />}
      {!error && !emptyData && notes.length > 0 && <PlainText className='ms-1' size={12} color={'var(--app-red)'} content={'Notes:'} />}
      {!error && !emptyData && notes.map((note, index) => <PlainText key={`note-${index}`} className='ms-4' size={12} color={'var(--app-red)'} content={note} />)}
    </EquipmentListManager.Container>
  )
};

/**
* @component EquipmentListManager.Container
* @description Styled container component for the equipment list.
* Centers content and provides consistent spacing.
*/
EquipmentListManager.Container = styled.div.attrs(props => ({
  id: 'equipmentlist',
  className: 'd-flex flex-column h-100 mt-5 m-auto'
}))`
  width: 90%;
`;

export default EquipmentListManager;